import { HttpClient } from '@angular/common/http';
import { Injectable, WritableSignal, inject, signal } from '@angular/core';
import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { catchError, map } from 'rxjs';

export interface PasswordViolations {
  hasRepetitiveChars: boolean;
  hasSequentialChars: boolean;
  missingSpecialChar: boolean;
  missingLowercase: boolean;
  missingUppercase: boolean;
  missingDigit: boolean;
  tooShort: boolean;
  tooLong: boolean;
  hasLeadingSpace: boolean;
  hasTrailingSpace: boolean;
  hasContextWords: boolean;
  containsUsername: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class PasswordService {
  private readonly http: HttpClient = inject(HttpClient);

  badPwList: string[] = [];
  breached: WritableSignal<boolean> = signal(false);

  constructor() {
    this.http
      .get('./assets/forbidden-passwords.txt', { responseType: 'text' })
      .pipe(
        map((badPasswords: string) => badPasswords.split(/[\r\n+]/)),
        catchError(() => [])
      )
      .subscribe((badPwList: string[]) => (this.badPwList = badPwList));
  }

  checkPasswordForViolations(password: string, username: string): PasswordViolations {
    const HAS_REPETITIVE_CHARS: RegExp = /(.)\1{2,}/;
    const HAS_SEQUENTIAL_CHARS: RegExp =
      /(abc|bcd|cde|def|efg|fgh|ghi|hij|ijk|jkl|klm|lmn|mno|nop|opq|pqr|qrs|rst|stu|tuv|uvw|vwx|wxy|xyz|012|123|234|345|456|567|678|789|987|876|765|654|543|432|321|210)+/i;
    const MISSING_SPECIAL_CHAR: RegExp = /^((?![(!@#$%^&()_+|~\-=*`{}[\]:";'<>?,./,\\)]).)*$/;
    const MISSING_LOWERCASE_CHAR: RegExp = /^((?![a-z]).)*$/;
    const MISSING_UPPERCASE_CHAR: RegExp = /^((?![A-Z]).)*$/;
    const MISSING_DIGIT: RegExp = /^((?![0-9]).)*$/;
    const TOO_SHORT: RegExp = /(^.{0,7}$)/;
    const TOO_LONG: RegExp = /(^.{257,}$)/;
    const HAS_LEADING_SPACE: RegExp = /(^\s+)/;
    const HAS_TRAILING_SPACE: RegExp = /(\s+$)/;
    const HAS_CONTEXT_WORDS: RegExp = /(EnlivenHealth|MarketTouch|FDS|Amplicare|Omnicell|Password)+/i;

    let containsUsername: boolean;

    if (!password.length || !username.length) {
      containsUsername = false;
    } else {
      containsUsername = password.toLowerCase().includes(username.toLowerCase());
    }

    return {
      hasRepetitiveChars: HAS_REPETITIVE_CHARS.test(password),
      hasSequentialChars: HAS_SEQUENTIAL_CHARS.test(password),
      missingSpecialChar: MISSING_SPECIAL_CHAR.test(password),
      missingLowercase: MISSING_LOWERCASE_CHAR.test(password),
      missingUppercase: MISSING_UPPERCASE_CHAR.test(password),
      missingDigit: MISSING_DIGIT.test(password),
      tooShort: TOO_SHORT.test(password),
      tooLong: TOO_LONG.test(password),
      hasLeadingSpace: HAS_LEADING_SPACE.test(password),
      hasTrailingSpace: HAS_TRAILING_SPACE.test(password),
      hasContextWords: HAS_CONTEXT_WORDS.test(password),
      containsUsername
    };
  }

  checkForBreach(pw: string) {
    this.breached.set(this.badPwList.includes(pw));
  }

  forbiddenPasswordValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const newPw: string = control.get('newPw').value;
      if (!newPw) return null;
      const username: string = control.get('username').value;
      const forbidden: boolean = Object.values(this.checkPasswordForViolations(newPw, username)).some(v => v);
      return forbidden ? { forbiddenPassword: true } : null;
    };
  }

  passwordsDoNotMatchValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const newPw: string = control.get('newPw').value;
      const pwConf: string = control.get('pwConf').value;
      if (!newPw || !pwConf) return null;
      return newPw !== pwConf ? { passwordsDoNotMatch: true } : null;
    };
  }
}
